/** Shared multi-country locale config for POS. */

export type CountryCode =
  | 'PK'
  | 'GB'
  | 'US'
  | 'DE'
  | 'FR'
  | 'ES'
  | 'IT'
  | 'NL'
  | 'BE'
  | 'IE'
  | 'AT'
  | 'PT'
  | 'GR'
  | 'FI'
  | 'PL'
  | 'SE'
  | 'DK'
  | 'CZ'
  | 'HU'
  | 'RO'
  | 'BG'
  | 'SA'
  | 'AE'
  | 'QA'
  | 'BH'
  | 'OM'
  | 'KW';

export type CurrencyCode =
  | 'PKR'
  | 'USD'
  | 'GBP'
  | 'EUR'
  | 'PLN'
  | 'SEK'
  | 'DKK'
  | 'CZK'
  | 'HUF'
  | 'RON'
  | 'BGN'
  | 'SAR'
  | 'AED'
  | 'QAR'
  | 'BHD'
  | 'OMR'
  | 'KWD';

export type UiLangCode = 'en' | 'ur' | 'ar' | 'de' | 'fr' | 'es' | 'it' | 'nl' | 'pl' | 'sv' | 'da' | 'cs' | 'hu' | 'ro' | 'bg';

export const GCC_COUNTRY_CODES = ['SA', 'AE', 'QA', 'BH', 'OM', 'KW'] as const;
export type GccCountryCode = (typeof GCC_COUNTRY_CODES)[number];

export interface CurrencyMeta {
  code: CurrencyCode;
  symbol: string;
  name: string;
}

export interface CountryMeta {
  code: CountryCode;
  name: string;
  currency: CurrencyCode;
  intlLocale: string;
  taxIdLabel: string;
  /** Secondary PK tax label (STRN/GST) */
  taxIdLabelSecondary?: string;
  localLanguage: UiLangCode;
  isEu: boolean;
  useCityMaster: boolean;
  usePostcodeLookup: boolean;
}

export interface BankOption {
  name: string;
  logo: string;
}

const favicon = (domain: string) =>
  `https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://${domain}&size=128`;

export const CURRENCIES: Record<CurrencyCode, CurrencyMeta> = {
  PKR: { code: 'PKR', symbol: 'Rs', name: 'Pakistani Rupee' },
  USD: { code: 'USD', symbol: '$', name: 'US Dollar' },
  GBP: { code: 'GBP', symbol: '£', name: 'British Pound' },
  EUR: { code: 'EUR', symbol: '€', name: 'Euro' },
  PLN: { code: 'PLN', symbol: 'zł', name: 'Polish Złoty' },
  SEK: { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
  DKK: { code: 'DKK', symbol: 'kr', name: 'Danish Krone' },
  CZK: { code: 'CZK', symbol: 'Kč', name: 'Czech Koruna' },
  HUF: { code: 'HUF', symbol: 'Ft', name: 'Hungarian Forint' },
  RON: { code: 'RON', symbol: 'lei', name: 'Romanian Leu' },
  BGN: { code: 'BGN', symbol: 'лв', name: 'Bulgarian Lev' },
  SAR: { code: 'SAR', symbol: '\u20C1', name: 'Saudi Riyal' },
  AED: { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  QAR: { code: 'QAR', symbol: 'ر.ق', name: 'Qatari Riyal' },
  BHD: { code: 'BHD', symbol: 'BD', name: 'Bahraini Dinar' },
  OMR: { code: 'OMR', symbol: 'ر.ع', name: 'Omani Rial' },
  KWD: { code: 'KWD', symbol: 'د.ك', name: 'Kuwaiti Dinar' },
};

export const COUNTRIES: CountryMeta[] = [
  { code: 'GB', name: 'United Kingdom', currency: 'GBP', intlLocale: 'en-GB', taxIdLabel: 'VAT Number', localLanguage: 'en', isEu: false, useCityMaster: false, usePostcodeLookup: true },
  { code: 'PK', name: 'Pakistan', currency: 'PKR', intlLocale: 'en-PK', taxIdLabel: 'NTN', taxIdLabelSecondary: 'GST / STRN', localLanguage: 'ur', isEu: false, useCityMaster: true, usePostcodeLookup: false },
  { code: 'US', name: 'United States', currency: 'USD', intlLocale: 'en-US', taxIdLabel: 'EIN', localLanguage: 'en', isEu: false, useCityMaster: false, usePostcodeLookup: false },
  { code: 'DE', name: 'Germany', currency: 'EUR', intlLocale: 'de-DE', taxIdLabel: 'VAT Number', localLanguage: 'de', isEu: true, useCityMaster: false, usePostcodeLookup: false },
  { code: 'FR', name: 'France', currency: 'EUR', intlLocale: 'fr-FR', taxIdLabel: 'VAT Number', localLanguage: 'fr', isEu: true, useCityMaster: false, usePostcodeLookup: false },
  { code: 'ES', name: 'Spain', currency: 'EUR', intlLocale: 'es-ES', taxIdLabel: 'VAT Number', localLanguage: 'es', isEu: true, useCityMaster: false, usePostcodeLookup: false },
  { code: 'IT', name: 'Italy', currency: 'EUR', intlLocale: 'it-IT', taxIdLabel: 'VAT Number', localLanguage: 'it', isEu: true, useCityMaster: false, usePostcodeLookup: false },
  { code: 'NL', name: 'Netherlands', currency: 'EUR', intlLocale: 'nl-NL', taxIdLabel: 'VAT Number', localLanguage: 'nl', isEu: true, useCityMaster: false, usePostcodeLookup: false },
  { code: 'BE', name: 'Belgium', currency: 'EUR', intlLocale: 'nl-BE', taxIdLabel: 'VAT Number', localLanguage: 'nl', isEu: true, useCityMaster: false, usePostcodeLookup: false },
  { code: 'IE', name: 'Ireland', currency: 'EUR', intlLocale: 'en-IE', taxIdLabel: 'VAT Number', localLanguage: 'en', isEu: true, useCityMaster: false, usePostcodeLookup: false },
  { code: 'AT', name: 'Austria', currency: 'EUR', intlLocale: 'de-AT', taxIdLabel: 'VAT Number', localLanguage: 'de', isEu: true, useCityMaster: false, usePostcodeLookup: false },
  { code: 'PT', name: 'Portugal', currency: 'EUR', intlLocale: 'pt-PT', taxIdLabel: 'VAT Number', localLanguage: 'en', isEu: true, useCityMaster: false, usePostcodeLookup: false },
  { code: 'GR', name: 'Greece', currency: 'EUR', intlLocale: 'el-GR', taxIdLabel: 'VAT Number', localLanguage: 'en', isEu: true, useCityMaster: false, usePostcodeLookup: false },
  { code: 'FI', name: 'Finland', currency: 'EUR', intlLocale: 'fi-FI', taxIdLabel: 'VAT Number', localLanguage: 'en', isEu: true, useCityMaster: false, usePostcodeLookup: false },
  { code: 'PL', name: 'Poland', currency: 'PLN', intlLocale: 'pl-PL', taxIdLabel: 'VAT Number', localLanguage: 'pl', isEu: true, useCityMaster: false, usePostcodeLookup: false },
  { code: 'SE', name: 'Sweden', currency: 'SEK', intlLocale: 'sv-SE', taxIdLabel: 'VAT Number', localLanguage: 'sv', isEu: true, useCityMaster: false, usePostcodeLookup: false },
  { code: 'DK', name: 'Denmark', currency: 'DKK', intlLocale: 'da-DK', taxIdLabel: 'VAT Number', localLanguage: 'da', isEu: true, useCityMaster: false, usePostcodeLookup: false },
  { code: 'CZ', name: 'Czech Republic', currency: 'CZK', intlLocale: 'cs-CZ', taxIdLabel: 'VAT Number', localLanguage: 'cs', isEu: true, useCityMaster: false, usePostcodeLookup: false },
  { code: 'HU', name: 'Hungary', currency: 'HUF', intlLocale: 'hu-HU', taxIdLabel: 'VAT Number', localLanguage: 'hu', isEu: true, useCityMaster: false, usePostcodeLookup: false },
  { code: 'RO', name: 'Romania', currency: 'RON', intlLocale: 'ro-RO', taxIdLabel: 'VAT Number', localLanguage: 'ro', isEu: true, useCityMaster: false, usePostcodeLookup: false },
  { code: 'BG', name: 'Bulgaria', currency: 'BGN', intlLocale: 'bg-BG', taxIdLabel: 'VAT Number', localLanguage: 'bg', isEu: true, useCityMaster: false, usePostcodeLookup: false },
  { code: 'SA', name: 'Saudi Arabia', currency: 'SAR', intlLocale: 'ar-SA', taxIdLabel: 'VAT / TRN', localLanguage: 'ar', isEu: false, useCityMaster: false, usePostcodeLookup: false },
  { code: 'AE', name: 'United Arab Emirates', currency: 'AED', intlLocale: 'ar-AE', taxIdLabel: 'TRN', localLanguage: 'ar', isEu: false, useCityMaster: false, usePostcodeLookup: false },
  { code: 'QA', name: 'Qatar', currency: 'QAR', intlLocale: 'ar-QA', taxIdLabel: 'TIN', localLanguage: 'ar', isEu: false, useCityMaster: false, usePostcodeLookup: false },
  { code: 'BH', name: 'Bahrain', currency: 'BHD', intlLocale: 'ar-BH', taxIdLabel: 'VAT Number', localLanguage: 'ar', isEu: false, useCityMaster: false, usePostcodeLookup: false },
  { code: 'OM', name: 'Oman', currency: 'OMR', intlLocale: 'ar-OM', taxIdLabel: 'VAT Number', localLanguage: 'ar', isEu: false, useCityMaster: false, usePostcodeLookup: false },
  { code: 'KW', name: 'Kuwait', currency: 'KWD', intlLocale: 'ar-KW', taxIdLabel: 'Tax ID', localLanguage: 'ar', isEu: false, useCityMaster: false, usePostcodeLookup: false },
];

export const REGIONS: Record<CountryCode, string[]> = {
  PK: [
    'Punjab',
    'Sindh',
    'Khyber Pakhtunkhwa',
    'Balochistan',
    'Islamabad Capital Territory',
    'Gilgit-Baltistan',
    'Azad Jammu and Kashmir',
  ],
  GB: ['England', 'Scotland', 'Wales', 'Northern Ireland'],
  US: [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
    'District of Columbia', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
    'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
    'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey',
    'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
    'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah',
    'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming',
  ],
  DE: ['Baden-Württemberg', 'Bayern', 'Berlin', 'Brandenburg', 'Bremen', 'Hamburg', 'Hessen', 'Mecklenburg-Vorpommern', 'Niedersachsen', 'Nordrhein-Westfalen', 'Rheinland-Pfalz', 'Saarland', 'Sachsen', 'Sachsen-Anhalt', 'Schleswig-Holstein', 'Thüringen'],
  FR: ['Île-de-France', 'Auvergne-Rhône-Alpes', 'Hauts-de-France', 'Grand Est', 'Nouvelle-Aquitaine', 'Occitanie', 'Provence-Alpes-Côte d\'Azur', 'Pays de la Loire', 'Bretagne', 'Normandie', 'Bourgogne-Franche-Comté', 'Centre-Val de Loire', 'Corse'],
  ES: ['Andalucía', 'Aragón', 'Asturias', 'Illes Balears', 'Canarias', 'Cantabria', 'Castilla-La Mancha', 'Castilla y León', 'Cataluña', 'Comunidad Valenciana', 'Extremadura', 'Galicia', 'Madrid', 'Murcia', 'Navarra', 'País Vasco', 'La Rioja'],
  IT: ['Lombardia', 'Lazio', 'Campania', 'Sicilia', 'Veneto', 'Emilia-Romagna', 'Piemonte', 'Puglia', 'Toscana', 'Calabria', 'Sardegna', 'Liguria', 'Marche', 'Abruzzo', 'Friuli-Venezia Giulia', 'Trentino-Alto Adige', 'Umbria', 'Basilicata', 'Molise', 'Valle d\'Aosta'],
  NL: ['Drenthe', 'Flevoland', 'Friesland', 'Gelderland', 'Groningen', 'Limburg', 'Noord-Brabant', 'Noord-Holland', 'Overijssel', 'Utrecht', 'Zeeland', 'Zuid-Holland'],
  BE: ['Brussels', 'Flanders', 'Wallonia'],
  IE: ['Leinster', 'Munster', 'Connacht', 'Ulster'],
  AT: ['Wien', 'Niederösterreich', 'Oberösterreich', 'Steiermark', 'Tirol', 'Kärnten', 'Salzburg', 'Vorarlberg', 'Burgenland'],
  PT: ['Norte', 'Centro', 'Lisboa', 'Alentejo', 'Algarve', 'Açores', 'Madeira'],
  GR: ['Attica', 'Central Macedonia', 'Crete', 'Thessaly', 'Western Greece', 'Peloponnese', 'Eastern Macedonia and Thrace'],
  FI: ['Uusimaa', 'Pirkanmaa', 'Varsinais-Suomi', 'North Ostrobothnia', 'Central Finland'],
  PL: ['Mazowieckie', 'Śląskie', 'Wielkopolskie', 'Małopolskie', 'Dolnośląskie', 'Łódzkie', 'Pomorskie', 'Lubelskie'],
  SE: ['Stockholm', 'Västra Götaland', 'Skåne', 'Östergötland', 'Uppsala', 'Jönköping'],
  DK: ['Capital Region', 'Central Denmark', 'North Denmark', 'Zealand', 'Southern Denmark'],
  CZ: ['Prague', 'Central Bohemia', 'South Moravia', 'Moravian-Silesian', 'Ústí nad Labem'],
  HU: ['Budapest', 'Pest', 'Borsod-Abaúj-Zemplén', 'Szabolcs-Szatmár-Bereg', 'Hajdú-Bihar'],
  RO: ['Bucharest', 'Cluj', 'Timiș', 'Iași', 'Constanța', 'Brașov'],
  BG: ['Sofia', 'Plovdiv', 'Varna', 'Burgas', 'Ruse'],
  SA: ['Riyadh', 'Makkah', 'Madinah', 'Eastern Province', 'Asir', 'Tabuk', 'Hail', 'Northern Borders', 'Jazan', 'Najran', 'Al Bahah', 'Al Jawf', 'Qassim'],
  AE: ['Abu Dhabi', 'Dubai', 'Sharjah', 'Ajman', 'Umm Al Quwain', 'Ras Al Khaimah', 'Fujairah'],
  QA: ['Doha', 'Al Rayyan', 'Al Wakrah', 'Al Khor', 'Umm Salal', 'Al Daayen', 'Al Shamal'],
  BH: ['Capital', 'Muharraq', 'Northern', 'Southern'],
  OM: ['Muscat', 'Dhofar', 'Musandam', 'Al Buraimi', 'Ad Dakhiliyah', 'Al Batinah North', 'Al Batinah South', 'Ash Sharqiyah North', 'Ash Sharqiyah South', 'Ad Dhahirah', 'Al Wusta'],
  KW: ['Capital', 'Hawalli', 'Farwaniya', 'Ahmadi', 'Jahra', 'Mubarak Al-Kabeer'],
};

const OTHER: BankOption = { name: 'Other', logo: '' };

export const BANKS_BY_COUNTRY: Partial<Record<CountryCode, BankOption[]>> = {
  PK: [
    { name: 'Habib Bank Limited (HBL)', logo: favicon('hbl.com') },
    { name: 'Meezan Bank', logo: favicon('meezanbank.com') },
    { name: 'Bank Alfalah', logo: favicon('bankalfalah.com') },
    { name: 'United Bank Limited (UBL)', logo: favicon('ubldigital.com') },
    { name: 'Allied Bank Limited (ABL)', logo: favicon('abl.com') },
    { name: 'MCB Bank', logo: favicon('mcb.com.pk') },
    { name: 'Standard Chartered Pakistan', logo: favicon('sc.com/pk') },
    { name: 'EasyPaisa', logo: favicon('easypaisa.com.pk') },
    { name: 'JazzCash', logo: favicon('jazzcash.com.pk') },
    { name: 'Faysal Bank', logo: favicon('faysalbank.com') },
    { name: 'Askari Bank', logo: favicon('askaribank.com') },
    { name: 'Bank Al Habib', logo: favicon('bankalhabib.com') },
    { name: 'NayaPay', logo: favicon('nayapay.com') },
    { name: 'SadaPay', logo: favicon('sadapay.pk') },
    { name: 'JS Bank', logo: favicon('jsbl.com') },
    { name: 'Soneri Bank', logo: favicon('soneribank.com') },
    { name: 'Habib Metropolitan Bank', logo: favicon('habibmetro.com') },
    { name: 'Dubai Islamic Bank Pakistan', logo: favicon('dibpak.com') },
    { name: 'Bank Islami', logo: favicon('bankislami.com.pk') },
    { name: 'National Bank of Pakistan (NBP)', logo: favicon('nbp.com.pk') },
    OTHER,
  ],
  US: [
    { name: 'Chase', logo: favicon('chase.com') },
    { name: 'Bank of America', logo: favicon('bankofamerica.com') },
    { name: 'Wells Fargo', logo: favicon('wellsfargo.com') },
    { name: 'Citibank', logo: favicon('citi.com') },
    { name: 'US Bank', logo: favicon('usbank.com') },
    { name: 'Capital One', logo: favicon('capitalone.com') },
    { name: 'PNC Bank', logo: favicon('pnc.com') },
    { name: 'TD Bank', logo: favicon('td.com') },
    OTHER,
  ],
  GB: [
    { name: 'Barclays', logo: favicon('barclays.co.uk') },
    { name: 'HSBC UK', logo: favicon('hsbc.co.uk') },
    { name: 'Lloyds Bank', logo: favicon('lloydsbank.com') },
    { name: 'NatWest', logo: favicon('natwest.com') },
    { name: 'Santander UK', logo: favicon('santander.co.uk') },
    { name: 'Nationwide', logo: favicon('nationwide.co.uk') },
    { name: 'Monzo', logo: favicon('monzo.com') },
    { name: 'Starling Bank', logo: favicon('starlingbank.com') },
    OTHER,
  ],
  DE: [
    { name: 'Deutsche Bank', logo: favicon('deutsche-bank.de') },
    { name: 'Commerzbank', logo: favicon('commerzbank.de') },
    { name: 'Sparkasse', logo: favicon('sparkasse.de') },
    { name: 'N26', logo: favicon('n26.com') },
    { name: 'ING Germany', logo: favicon('ing.de') },
    OTHER,
  ],
  FR: [
    { name: 'BNP Paribas', logo: favicon('bnpparibas.com') },
    { name: 'Crédit Agricole', logo: favicon('credit-agricole.fr') },
    { name: 'Société Générale', logo: favicon('societegenerale.com') },
    { name: 'Banque Populaire', logo: favicon('banquepopulaire.fr') },
    OTHER,
  ],
  ES: [
    { name: 'Santander', logo: favicon('santander.com') },
    { name: 'BBVA', logo: favicon('bbva.com') },
    { name: 'CaixaBank', logo: favicon('caixabank.com') },
    { name: 'Sabadell', logo: favicon('bancsabadell.com') },
    OTHER,
  ],
  IT: [
    { name: 'Intesa Sanpaolo', logo: favicon('intesasanpaolo.com') },
    { name: 'UniCredit', logo: favicon('unicredit.it') },
    { name: 'Poste Italiane', logo: favicon('poste.it') },
    OTHER,
  ],
  NL: [
    { name: 'ING', logo: favicon('ing.nl') },
    { name: 'Rabobank', logo: favicon('rabobank.nl') },
    { name: 'ABN AMRO', logo: favicon('abnamro.nl') },
    OTHER,
  ],
  SA: [
    { name: 'Al Rajhi Bank', logo: favicon('alrajhibank.com.sa') },
    { name: 'Saudi National Bank (SNB)', logo: favicon('alahlionline.com') },
    { name: 'Riyad Bank', logo: favicon('riyadbank.com') },
    { name: 'Saudi British Bank (SABB)', logo: favicon('sabb.com') },
    { name: 'Banque Saudi Fransi', logo: favicon('alfransi.com.sa') },
    OTHER,
  ],
  AE: [
    { name: 'Emirates NBD', logo: favicon('emiratesnbd.com') },
    { name: 'FAB', logo: favicon('bankfab.com') },
    { name: 'ADCB', logo: favicon('adcb.com') },
    { name: 'Dubai Islamic Bank', logo: favicon('dib.ae') },
    { name: 'Mashreq', logo: favicon('mashreq.com') },
    OTHER,
  ],
};

export const LANG_LABELS: Record<string, string> = {
  en: 'English',
  ur: 'Urdu',
  de: 'German',
  fr: 'French',
  es: 'Spanish',
  it: 'Italian',
  nl: 'Dutch',
  pl: 'Polish',
  sv: 'Swedish',
  da: 'Danish',
  cs: 'Czech',
  hu: 'Hungarian',
  ro: 'Romanian',
  bg: 'Bulgarian',
  ar: 'Arabic',
};

export function getCountry(code?: string | null): CountryMeta {
  const normalized = String(code || '').trim().toUpperCase();
  return (
    COUNTRIES.find((c) => c.code === normalized) ||
    COUNTRIES.find((c) => c.code === 'GB') ||
    COUNTRIES[0]
  );
}

export function getCurrencyMeta(code?: string | null): CurrencyMeta {
  if (code && code in CURRENCIES) return CURRENCIES[code as CurrencyCode];
  return CURRENCIES.GBP;
}

export function getRegionsForCountry(code?: string | null): string[] {
  const c = (code || 'GB') as CountryCode;
  return REGIONS[c] || [];
}

export function getBanksForCountry(code?: string | null): BankOption[] {
  const c = (code || 'GB') as CountryCode;
  return BANKS_BY_COUNTRY[c] || BANKS_BY_COUNTRY.GB || [OTHER];
}

export function resolveBankLogo(bankName: string, logoUrl?: string | null, country?: string | null): string {
  if (logoUrl) return logoUrl;
  const list = getBanksForCountry(country);
  return list.find((b) => b.name === bankName)?.logo || '';
}

/** Common cash note / coin shortcuts for POS checkout keypad (by currency). */
export const CASH_NOTES_BY_CURRENCY: Record<CurrencyCode, number[]> = {
  GBP: [1, 2, 5, 10, 20, 50],
  PKR: [10, 20, 50, 100, 500, 1000, 5000],
  USD: [1, 5, 10, 20, 50, 100],
  EUR: [5, 10, 20, 50, 100, 200],
  PLN: [10, 20, 50, 100, 200, 500],
  SEK: [20, 50, 100, 200, 500],
  DKK: [50, 100, 200, 500],
  CZK: [100, 200, 500, 1000, 2000],
  HUF: [500, 1000, 2000, 5000, 10000, 20000],
  RON: [1, 5, 10, 50, 100, 200],
  BGN: [2, 5, 10, 20, 50, 100],
  SAR: [1, 5, 10, 50, 100, 500],
  AED: [5, 10, 20, 50, 100, 200, 500],
  QAR: [1, 5, 10, 50, 100, 200, 500],
  BHD: [0.5, 1, 5, 10, 20],
  OMR: [0.1, 0.5, 1, 5, 10, 20, 50],
  KWD: [0.25, 0.5, 1, 5, 10, 20],
};

export function getCashNotesForCurrency(currency?: string | null): number[] {
  if (currency && currency in CASH_NOTES_BY_CURRENCY) {
    return CASH_NOTES_BY_CURRENCY[currency as CurrencyCode];
  }
  return CASH_NOTES_BY_CURRENCY.GBP;
}

export function getCashNotesForCountry(country?: string | null): number[] {
  return getCashNotesForCurrency(getCountry(country).currency);
}

/** All supported UI languages (not filtered by country). */
export function getAllUiLanguages(): { code: UiLangCode; label: string }[] {
  return (Object.keys(LANG_LABELS) as UiLangCode[]).map((code) => ({
    code,
    label: LANG_LABELS[code] || code,
  }));
}

/** Selected language first; remaining options sorted A–Z by label. */
export function sortUiLanguagesWithSelectedFirst(
  languages: { code: UiLangCode; label: string }[],
  selectedCode?: string | null,
): { code: UiLangCode; label: string }[] {
  const selected = languages.find((l) => l.code === selectedCode);
  const rest = languages
    .filter((l) => l.code !== selectedCode)
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  return selected ? [selected, ...rest] : rest;
}

/** Current selection first, country local language second, then rest A–Z by label. */
export function sortUiLanguagesForPicker(
  languages: { code: UiLangCode; label: string }[],
  options?: { selectedCode?: string | null; countryCode?: string | null },
): { code: UiLangCode; label: string }[] {
  const { selectedCode, countryCode } = options || {};
  const countryLang = getCountry(countryCode).localLanguage;
  const pinned = new Set<UiLangCode>();
  const result: { code: UiLangCode; label: string }[] = [];

  const push = (lang?: { code: UiLangCode; label: string }) => {
    if (lang && !pinned.has(lang.code)) {
      result.push(lang);
      pinned.add(lang.code);
    }
  };

  push(languages.find((l) => l.code === selectedCode));
  push(languages.find((l) => l.code === countryLang));

  const rest = languages
    .filter((l) => !pinned.has(l.code))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

  return [...result, ...rest];
}

/** English + local language only (PK: en|ur; EU: en|local; UK/US: en). */
export function getUiLanguagesForCountry(code?: string | null): { code: UiLangCode; label: string }[] {
  const country = getCountry(code);
  const opts: { code: UiLangCode; label: string }[] = [{ code: 'en', label: LANG_LABELS.en }];
  if (country.localLanguage && country.localLanguage !== 'en') {
    opts.push({
      code: country.localLanguage,
      label: LANG_LABELS[country.localLanguage] || country.localLanguage,
    });
  }
  return opts;
}

export function isLanguageAllowed(_countryCode: string | null | undefined, lang: string | null | undefined): boolean {
  return Boolean(lang && lang in LANG_LABELS);
}

export function isPakistan(code?: string | null): boolean {
  return String(code || '').toUpperCase() === 'PK';
}

export function isUk(code?: string | null): boolean {
  const c = String(code || '').toUpperCase();
  return c === 'GB' || c === 'UK';
}

/**
 * Map product tax_rate % to UK VAT rate letter used on trade invoices.
 * S = Standard 20%, R = Reduced 5%, Z = Zero-rated 0%. Other rates show as "n%".
 */
export function ukVatRateCode(taxRatePercent?: number | null): string {
  const raw = Number(taxRatePercent);
  if (!Number.isFinite(raw) || raw <= 0) return 'Z';
  // Accept fraction (0.20) or percent (20)
  const pct = raw > 0 && raw <= 1 ? raw * 100 : raw;
  if (Math.abs(pct - 20) < 0.51) return 'S';
  if (Math.abs(pct - 5) < 0.51) return 'R';
  if (Math.abs(pct) < 0.51) return 'Z';
  const rounded = Math.round(pct * 100) / 100;
  return `${rounded % 1 === 0 ? String(rounded) : rounded.toFixed(2)}%`;
}

export function isEuOrUk(code?: string | null): boolean {
  const c = getCountry(code);
  return c.isEu || c.code === 'GB';
}

export function isGcc(code?: string | null): boolean {
  return GCC_COUNTRY_CODES.includes((code || '') as GccCountryCode);
}

export function isSaudiArabia(code?: string | null): boolean {
  return String(code || '').toUpperCase() === 'SA';
}

export function isUae(code?: string | null): boolean {
  return (code || '') === 'AE';
}

export function isMiddleEast(code?: string | null): boolean {
  return isGcc(code);
}

/** Standard VAT/sales-tax rates (%). Zero where no single national rate applies in POS. */
export const DEFAULT_VAT_RATES: Partial<Record<CountryCode, number>> = {
  SA: 15,
  AE: 5,
  BH: 10,
  OM: 5,
  GB: 20,
  DE: 19,
  FR: 20,
  ES: 21,
  IT: 22,
  NL: 21,
  BE: 21,
  IE: 23,
  AT: 20,
  PT: 23,
  GR: 24,
  FI: 25.5,
  PL: 23,
  SE: 25,
  DK: 25,
  CZ: 21,
  HU: 27,
  RO: 19,
  BG: 20,
};

export interface CountryTaxDefaults {
  defaultVatRate: number;
  pricesIncludeTax: boolean;
  enableItemTax: boolean;
}

export function getDefaultVatRate(code?: string | null): number {
  const c = (code || 'GB').toUpperCase() as CountryCode;
  return DEFAULT_VAT_RATES[c] ?? 0;
}

/**
 * Whether shelf prices include tax by default.
 * UK: exclusive (VAT on top) — UK has Z/R/S categories, not one shelf-inclusive rate.
 * PK / US: exclusive. Other VAT countries: inclusive retail default.
 */
export function getDefaultPricesIncludeTax(code?: string | null): boolean {
  const c = (code || 'GB').toUpperCase();
  if (c === 'GB' || c === 'UK' || c === 'PK' || c === 'US') return false;
  return true;
}

/** UK VAT rate bands used on trade invoices (Z / R / S). */
export const UK_VAT_CATEGORIES = [
  { code: 'Z', label: 'Zero-rated (Z)', rate: 0 },
  { code: 'R', label: 'Reduced 5% (R)', rate: 5 },
  { code: 'S', label: 'Standard 20% (S)', rate: 20 },
] as const;

export type UkVatCategoryCode = (typeof UK_VAT_CATEGORIES)[number]['code'] | 'custom';

export function ukVatCategoryFromRate(taxRatePercent?: number | null): UkVatCategoryCode {
  const raw = Number(taxRatePercent);
  if (!Number.isFinite(raw) || raw <= 0) return 'Z';
  const pct = raw > 0 && raw <= 1 ? raw * 100 : raw;
  if (Math.abs(pct - 20) < 0.51) return 'S';
  if (Math.abs(pct - 5) < 0.51) return 'R';
  if (Math.abs(pct) < 0.51) return 'Z';
  return 'custom';
}

/** Auto-sum line tax from product tax_rate when country has a standard VAT rate. */
export function getDefaultEnableItemTax(code?: string | null): boolean {
  return getDefaultVatRate(code) > 0;
}

export function getCountryTaxDefaults(code?: string | null): CountryTaxDefaults {
  return {
    defaultVatRate: getDefaultVatRate(code),
    pricesIncludeTax: getDefaultPricesIncludeTax(code),
    enableItemTax: getDefaultEnableItemTax(code),
  };
}

/** Default IANA timezone per supported country (matches backend locale_helpers). */
export const COUNTRY_TIMEZONE: Record<CountryCode, string> = {
  PK: 'Asia/Karachi',
  GB: 'Europe/London',
  US: 'America/New_York',
  DE: 'Europe/Berlin',
  FR: 'Europe/Paris',
  ES: 'Europe/Madrid',
  IT: 'Europe/Rome',
  NL: 'Europe/Amsterdam',
  BE: 'Europe/Brussels',
  IE: 'Europe/Dublin',
  AT: 'Europe/Vienna',
  PT: 'Europe/Lisbon',
  GR: 'Europe/Athens',
  FI: 'Europe/Helsinki',
  PL: 'Europe/Warsaw',
  SE: 'Europe/Stockholm',
  DK: 'Europe/Copenhagen',
  CZ: 'Europe/Prague',
  HU: 'Europe/Budapest',
  RO: 'Europe/Bucharest',
  BG: 'Europe/Sofia',
  SA: 'Asia/Riyadh',
  AE: 'Asia/Dubai',
  QA: 'Asia/Qatar',
  BH: 'Asia/Bahrain',
  OM: 'Asia/Muscat',
  KW: 'Asia/Kuwait',
};

export function getTimezoneForCountry(code?: string | null): string {
  const c = (code || 'GB').toUpperCase() as CountryCode;
  return COUNTRY_TIMEZONE[c] || 'Europe/London';
}

/** Unique timezone options for company settings (value = IANA id). */
export function getTimezoneOptions(): { value: string; label: string }[] {
  const seen = new Set<string>();
  const opts: { value: string; label: string }[] = [];
  for (const tz of Object.values(COUNTRY_TIMEZONE)) {
    if (seen.has(tz)) continue;
    seen.add(tz);
    opts.push({ value: tz, label: tz.replace(/_/g, ' ') });
  }
  return opts.sort((a, b) => a.label.localeCompare(b.label));
}
